<template>
    <ModalOverlay @click.stop>
        <ModalContainer @click.stop>
            <ModalHeader>
                <ModalTitle>
                    <MessageIcon>📱</MessageIcon>
                    문자 발송
                </ModalTitle>
                <CloseButton @click="closeModal">
                    <Close :size="24" />
                </CloseButton>
            </ModalHeader>

            <ModalBody>
                <ModalBodyGrid>
                    <!-- 왼쪽 섹션: 입력 영역 -->
                    <LeftSection>
                        <!-- * 발신자/수신자 정보 (한 줄) -->
                        <ContactInfoSection>
                            <SectionTitle>연락처 정보</SectionTitle>
                            <ContactInfoRow>
                                <SenderCard>
                                    <SenderInfo>
                                        <SenderLabel>발신자</SenderLabel>
                                        <SenderNameRow>
                                            <SenderName>{{ $globalState.user_name || '발신자명' }}</SenderName>
                                            <SenderPhone>{{ formatPhoneNumber($globalState.user_phone || '') }}
                                            </SenderPhone>
                                        </SenderNameRow>
                                    </SenderInfo>
                                </SenderCard>
                                <RecipientCard>
                                    <RecipientInfo>
                                        <RecipientLabel>수신자</RecipientLabel>
                                        <RecipientNameRow>
                                            <RecipientName>{{ isTestUser ? '테스트' : recipient.name }}</RecipientName>
                                            <RecipientPhone>{{ formatPhoneNumber(isTestUser ? '010-0000-0000' :
                                                recipient.phone) }}</RecipientPhone>
                                        </RecipientNameRow>
                                    </RecipientInfo>
                                </RecipientCard>
                                <FaxNumberCard>
                                    <FaxNumberInfo>
                                        <FaxNumberLabel>팩스번호</FaxNumberLabel>
                                        <FaxNumberInput v-model="faxNumber" type="text" placeholder="팩스번호를 입력하세요"
                                            :disabled="sendMethod !== 'fax'" @input="formatFaxNumber"
                                            @keypress="onlyNumbers" />
                                    </FaxNumberInfo>
                                </FaxNumberCard>
                            </ContactInfoRow>
                        </ContactInfoSection>

                        <!-- * 발송 방법 선택 (한 줄) -->
                        <SendMethodSection>
                            <SectionTitle>발송 방법</SectionTitle>
                            <MethodButtons>
                                <MethodButton :class="{ active: sendMethod === 'sms' }" @click="sendMethod = 'sms'">
                                    <MethodIcon>📱</MethodIcon>
                                    <MethodText>문자메시지</MethodText>
                                </MethodButton>
                                <MethodButton :class="{ active: sendMethod === 'kakao' }" @click="sendMethod = 'kakao'">
                                    <MethodIcon>💬</MethodIcon>
                                    <MethodText>카카오톡</MethodText>
                                </MethodButton>
                                <MethodButton :class="{ active: sendMethod === 'fax' }" @click="sendMethod = 'fax'">
                                    <MethodIcon>📠</MethodIcon>
                                    <MethodText>팩스</MethodText>
                                </MethodButton>
                            </MethodButtons>
                        </SendMethodSection>

                        <!-- * 메시지 입력 -->
                        <MessageSection>
                            <SectionTitle>
                                {{ sendMethod === 'fax' ? '팩스 제목' : '메시지 내용' }}
                                <CharacterCount v-if="sendMethod !== 'fax'">{{ message.length }}/{{ maxLength }}
                                </CharacterCount>
                                <CharacterCount v-else>{{ message.length }}/40</CharacterCount>
                            </SectionTitle>
                            <MessageTextarea v-model="message" :maxlength="sendMethod === 'fax' ? 40 : maxLength"
                                :placeholder="sendMethod === 'fax' ? '팩스 제목을 입력하세요...' : '전송할 메시지를 입력하세요...'" />

                            <MessageTemplates v-if="sendMethod !== 'fax'">
                                <TemplateButton v-for="template in messageTemplates" :key="template.id"
                                    @click="applyTemplate(template.content)">
                                    {{ template.name }}
                                </TemplateButton>
                            </MessageTemplates>
                        </MessageSection>
                    </LeftSection>

                    <!-- 오른쪽 섹션: 버튼 및 파일 첨부 -->
                    <RightSection>
                        <SectionTitle>전송 내역</SectionTitle>
                        <!-- * 전송내역 조회 버튼 -->
                        <HistoryButtonsSection>
                            <HistoryButtonSms @click="sendSMSList">
                                <Forum :size="20" />문자발송내역
                            </HistoryButtonSms>
                            <HistoryButtonFax @click="sendFaxList">
                                <Fax :size="20" />팩스발송내역
                            </HistoryButtonFax>
                            <HistoryButtonKakao @click="sendKakaoList">
                                <Chat :size="20" />알림톡발송내역
                            </HistoryButtonKakao>
                        </HistoryButtonsSection>

                        <!-- * 파일 첨부 섹션 -->
                        <FileAttachmentSection>
                            <FileAttachmentHeader>
                                <SectionTitle>파일 첨부</SectionTitle>
                                <FileInputButton @click="openFileDialog">
                                    📎 파일 첨부
                                </FileInputButton>
                            </FileAttachmentHeader>

                            <input type="file" accept="image/jpeg,image/jpg,image/png" @change="handleFileSelect"
                                ref="fileInput" style="display: none;" />

                            <FileAttachmentContent>
                                <!-- 사용자가 직접 등록한 파일 -->
                                <UserFileInfo v-if="selectedFile">
                                    <UserFileLabel>직접 등록한 파일</UserFileLabel>
                                    <FileInfo>
                                        {{ selectedFile.name }} ({{ formatFileSize(selectedFile.size) }})
                                        <RemoveFileButton @click="removeFile">✕</RemoveFileButton>
                                    </FileInfo>
                                </UserFileInfo>

                                <FileHelpText v-if="!selectedFile && (!orderFiles || orderFiles.length === 0)">
                                    주문서에 등록된 파일을 선택하거나 직접 파일을 첨부할 수 있습니다.
                                </FileHelpText>

                                <!-- 주문서에서 전달받은 파일 목록 -->
                                <OrderFilesList v-if="orderFiles && orderFiles.length > 0">
                                    <OrderFilesTitle>주문서에 등록된 파일</OrderFilesTitle>
                                    <OrderFileItem v-for="(file, index) in orderFiles" :key="index">
                                        <OrderFileCheckbox>
                                            <input type="checkbox" v-model="file.selected"
                                                :id="`order-file-${index}`" />
                                            <label :for="`order-file-${index}`"></label>
                                        </OrderFileCheckbox>
                                        <OrderFileInfo>
                                            <OrderFileName>{{ file.fileName }}</OrderFileName>
                                            <OrderFileDate>{{ formatDate(file.registeredDate) }}</OrderFileDate>
                                        </OrderFileInfo>
                                        <OrderFilePreview v-if="isImageFile(file.fileName)" :src="file.url"
                                            @click="openFilePreview(file)" />
                                        <OrderFileIcon v-else-if="isPdfFile(file.fileName)">PDF</OrderFileIcon>
                                        <OrderFileIcon v-else>FILE</OrderFileIcon>
                                    </OrderFileItem>
                                </OrderFilesList>
                            </FileAttachmentContent>
                        </FileAttachmentSection>
                    </RightSection>
                </ModalBodyGrid>
            </ModalBody>

            <ModalFooter>
                <FooterButtons>
                    <CancelButton @click="closeModal">취소</CancelButton>
                    <SendButton :class="{ kakao: sendMethod === 'kakao', sms: sendMethod === 'sms' }"
                        @click="sendMessage" :disabled="!canSend">
                        <Send :size="15" :style="{ paddingTop: '2px' }" />
                        {{ sendMethod === 'kakao' ? '카카오톡 발송' : sendMethod === 'fax' ? '팩스 발송' : '문자 발송' }}
                    </SendButton>
                </FooterButtons>
            </ModalFooter>
        </ModalContainer>
    </ModalOverlay>
</template>

<script>
import * as SmsSendModalCss from '@/assets/styles/module/SmsSendModalCss.js';

import Close from "vue-material-design-icons/Close.vue";
import Send from "vue-material-design-icons/Send.vue";
import Forum from "vue-material-design-icons/Forum.vue";
import Fax from "vue-material-design-icons/Fax.vue";
import Chat from "vue-material-design-icons/Chat.vue";

export default {
    name: 'SmsSendModal',

    components: {
        ...SmsSendModalCss,
        Close, Send, Forum, Fax, Chat
    },

    props: {
        recipient: {
            type: Object,
            required: true,
            validator: (value) => {
                return value.name && (value.phone1 || value.phone2);
            }
        },
        orderFiles: {
            type: Array,
            default: () => []
        }
    },

    emits: ['close', 'send'],

    data() {
        return {
            sendMethod: 'sms',
            message: '',
            maxLength: 2000,
            selectedFile: null, // 선택된 파일
            filePreview: null, // 파일 미리보기 URL
            faxNumber: '',
            messageTemplates: [
                {
                    id: 1,
                    name: '기본 인사',
                    content: '안녕하세요 {name}님 {company}입니다.\n보낼 문자를 자유롭게 입력해주세요.\n감사합니다.'
                },
                { id: 2, name: '산소 서류준비', content: '아래 서류를 준비하여 {company}에 전달해주세요. \n- 처방전\n- 신분증' },
                { id: 3, name: '처방전 만료', content: '곧 처방전 사용기간이 만료됩니다. \n처방전 재발급 후 {company}에 전달 부탁드립니다.' },
            ]
        };
    },

    computed: {
        isTestUser() {
            try {
                const userInfo = JSON.parse(localStorage.getItem('userInfo'));
                return userInfo && userInfo.id === 'test';
            } catch (error) {
                return false;
            }
        },

        canSend() {
            return this.message.trim().length > 0;
        }
    },

    methods: {
        isImageFile(fileName) {
            if (!fileName) return false;
            const ext = fileName.toLowerCase().split('.').pop();
            return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext);
        },

        isPdfFile(fileName) {
            if (!fileName) return false;
            return fileName.toLowerCase().endsWith('.pdf');
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            try {
                const date = new Date(dateString);
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            } catch (error) {
                return dateString;
            }
        },

        openFilePreview(file) {
            if (this.isImageFile(file.fileName)) {
                window.open(file.url, '_blank');
            } else if (this.isPdfFile(file.fileName)) {
                window.open(file.url, '_blank');
            }
        },

        openFileDialog() {
            if (this.$refs.fileInput) {
                this.$refs.fileInput.click();
            }
        },

        handleFileSelect(event) {
            const file = event.target.files[0];
            if (!file) return;

            // 파일 크기 제한 (300KB)
            if (file.size > 300 * 1024) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '파일 크기 초과',
                    text: '이미지 파일은 300KB 이하여야 합니다.',
                    confirmButtonText: '확인'
                });
                return;
            }

            // 파일 타입 확인
            if (!file.type.match(/^image\/(jpeg|jpg|png)$/)) {
                this.$swal.fire({
                    icon: 'warning',
                    title: '지원하지 않는 파일 형식',
                    text: 'JPEG, JPG, PNG 형식만 지원합니다.',
                    confirmButtonText: '확인'
                });
                return;
            }

            this.selectedFile = file;

            // 미리보기 생성
            const reader = new FileReader();
            reader.onload = (e) => {
                this.filePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },

        removeFile() {
            this.selectedFile = null;
            this.filePreview = null;
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }
        },

        formatFileSize(bytes) {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },

        getItemTypeLabel(item) {
            const labels = {
                'SMS': '단문',
                'LMS': '장문',
                'MMS': '포토문자'
            };
            return labels[item] || item;
        },

        getStatusLabel(state) {
            const labels = {
                '1': '대기',
                '2': '성공',
                '3': '실패',
                '4': '취소'
            };
            // state가 숫자로 올 수 있으므로 문자열로 변환
            const stateStr = String(state);
            return labels[stateStr] || stateStr;
        },

        getStatusClass(state) {
            const classes = {
                '1': 'status-waiting',
                '2': 'status-success',
                '3': 'status-failed',
                '4': 'status-cancelled'
            };
            // state가 숫자로 올 수 있으므로 문자열로 변환
            const stateStr = String(state);
            return classes[stateStr] || '';
        },

        truncateContent(content) {
            if (!content) return '-';
            return content.length > 50 ? content.substring(0, 50) + '...' : content;
        },

        async sendMessage() {
            try {
                const sendData = {
                    method: this.sendMethod,
                    recipient: {
                        name: this.recipient.name,
                        phone: this.recipient.phone,
                        companykey: this.recipient.companykey
                    },
                    message: this.message.trim(),
                    subject: this.message.trim().substring(0, 40) // MMS 제목 (최대 40자)
                };

                let response;
                if (this.sendMethod === 'kakao') {
                    response = await this.sendKakaoMessage(sendData);
                } else if (this.sendMethod === 'fax') {
                    // 팩스 전송
                    response = await this.sendFaxMessage(sendData);
                } else if (this.selectedFile) {
                    // 파일이 있으면 MMS로 전송
                    response = await this.sendMMSMessage(sendData);
                } else {
                    // 파일이 없으면 SMS로 전송
                    response = await this.sendSMSMessage(sendData);
                }

                if (response && response.success) {
                    const messageType = this.sendMethod === 'fax' ? '팩스' :
                        (this.selectedFile ? '포토문자' :
                            (this.sendMethod === 'kakao' ? '알림톡' : '문자메시지'));
                    this.$swal.fire({
                        title: '전송 완료',
                        text: `${messageType}가 성공적으로 전송되었습니다.`,
                        icon: 'success'
                    });
                    this.$emit('send', sendData);
                    // 전송 후 내역 새로고침 및 파일 초기화
                    this.removeFile();
                } else {
                    throw new Error(response?.message || '메시지 전송에 실패했습니다.');
                }
            } catch (error) {
                console.error('메시지 전송 실패:', error);

                this.$swal.fire({
                    title: '전송 실패',
                    text: error.response?.data?.message || '메시지 전송 중 오류가 발생했습니다.',
                    icon: 'error'
                });
            }
        },

        async sendFaxMessage(sendData) {
            try {
                // 팩스 번호 확인 (입력된 팩스 번호 우선, 없으면 recipient.phone 사용)
                const faxNumberToUse = this.faxNumber.trim() || sendData.recipient.phone;

                if (!faxNumberToUse) {
                    throw new Error('팩스 번호를 입력해주세요.');
                }

                // 선택된 파일 확인 (주문서 파일 또는 직접 등록한 파일)
                const selectedOrderFiles = this.orderFiles?.filter(f => f.selected) || [];
                const hasUserFile = !!this.selectedFile;

                if (selectedOrderFiles.length === 0 && !hasUserFile) {
                    throw new Error('팩스 전송을 위한 파일을 선택하거나 첨부해주세요.');
                }

                // 전송할 파일 목록 준비
                const filesToSend = [];

                // 주문서에서 선택된 파일들 추가
                for (const orderFile of selectedOrderFiles) {
                    try {
                        const response = await fetch(orderFile.url);
                        if (!response.ok) {
                            throw new Error(`파일 다운로드 실패: ${response.status}`);
                        }
                        const blob = await response.blob();

                        // 빈 파일 체크
                        if (blob.size === 0) {
                            throw new Error(`파일이 비어있습니다: ${orderFile.fileName}`);
                        }

                        const file = new File([blob], orderFile.fileName, {
                            type: blob.type || 'application/octet-stream'
                        });
                        filesToSend.push(file);
                    } catch (error) {
                        console.error('주문서 파일 로드 실패:', orderFile.fileName, error);
                        throw new Error(`파일 "${orderFile.fileName}"을 불러올 수 없습니다.`);
                    }
                }

                // 사용자가 직접 등록한 파일 추가
                if (hasUserFile) {
                    // 파일 유효성 검증
                    if (this.selectedFile.size === 0) {
                        throw new Error('선택한 파일이 비어있습니다.');
                    }
                    filesToSend.push(this.selectedFile);
                }

                // 최대 20개 파일 제한 확인
                if (filesToSend.length > 20) {
                    throw new Error('팩스 전송은 최대 20개 파일까지 가능합니다.');
                }

                // FormData 생성
                const formData = new FormData();

                // 팩스 번호에서 하이픈 제거
                const cleanFaxNumber = faxNumberToUse.replace(/-/g, '');

                const jsonData = {
                    snd: this.$globalState.company_fax, // 발신번호
                    sndnm: this.$globalState.company, // 발신자명
                    rcvs: [{
                        rcv: cleanFaxNumber, // 수신번호 (입력된 팩스 번호 사용)
                        rcvnm: sendData.recipient.name // 수신자명
                    }],
                    title: sendData.message.trim().substring(0, 40), // 팩스 제목
                    sndDT: '', // 발송일시 (빈 값이면 즉시 발송)
                    adsYN: false, // 광고성 팩스 여부
                    fcnt: filesToSend.length.toString(), // 파일 개수
                    requestNum: '' // 요청번호 (선택사항)
                };
                formData.append('form', JSON.stringify(jsonData));

                if (this.$globalState.company_bnum) {
                    formData.append('company_bnum', this.$globalState.company_bnum);
                }

                // 파일들 추가 (반드시 form, company_bnum 이후에 추가)
                for (let i = 0; i < filesToSend.length; i++) {
                    const file = filesToSend[i];
                    // 파일 유효성 재확인
                    if (!file || file.size === 0) {
                        throw new Error(`파일 "${file?.name || 'unknown'}"이 유효하지 않습니다.`);
                    }
                    // 파일명이 없으면 기본값 설정
                    const fileName = file.name || `file_${i + 1}`;
                    formData.append('file', file, fileName);
                }

                // FormData 검증 (디버깅용)
                for (const [key, value] of formData.entries()) {
                    if (value instanceof File) {
                        console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
                    } else {
                        console.log(`  ${key}: ${typeof value === 'string' ? value.substring(0, 100) : value}`);
                    }
                }

                // 팩스 전송 API 호출
                const response = await this.$axios.post(
                    `${this.$apiUrls.hanqmedi}/popbill/fax/sendFax`,
                    formData,
                    {
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            'x-pb-userid': this.$globalState.popbill_id
                        }
                    }
                );

                return {
                    success: true,
                    receiptNum: response.data.receiptNum || response.data.receiptNumList?.[0]
                };
            } catch (error) {
                const errorMessage = error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message ||
                    '팩스 전송 중 오류가 발생했습니다.';

                throw new Error(errorMessage);
            }
        },

        async sendMMSMessage(sendData) {
            try {
                if (!sendData.recipient.phone) {
                    throw new Error('수신번호가 없습니다.');
                }

                if (!sendData.message) {
                    throw new Error('메시지 내용을 입력해주세요.');
                }

                // 선택된 파일 확인 (주문서 파일 또는 직접 등록한 파일)
                const selectedOrderFiles = this.orderFiles?.filter(f => f.selected) || [];
                const hasUserFile = !!this.selectedFile;

                if (selectedOrderFiles.length === 0 && !hasUserFile) {
                    throw new Error('이미지 파일을 선택하거나 첨부해주세요.');
                }

                // 주문서 파일이 선택된 경우 첫 번째 파일 사용
                let fileToSend = this.selectedFile;
                if (selectedOrderFiles.length > 0 && !hasUserFile) {
                    // 주문서 파일을 Blob으로 변환
                    const response = await fetch(selectedOrderFiles[0].url);
                    const blob = await response.blob();
                    fileToSend = new File([blob], selectedOrderFiles[0].fileName, { type: blob.type });
                }

                // FormData 생성
                const formData = new FormData();
                formData.append('file', fileToSend);

                // JSON 데이터 추가
                const jsonData = {
                    snd: this.$globalState.user_phone,
                    sndnm: this.$globalState.user_name,
                    content: sendData.message,
                    sndDT: '',
                    adsYN: false,
                    requestNum: '',
                    msgs: [{
                        rcv: sendData.recipient.phone,
                        rcvnm: sendData.recipient.name
                    }]
                };

                const formJsonString = JSON.stringify(jsonData);
                formData.append('form', formJsonString);

                if (this.$globalState.company_bnum) {
                    formData.append('company_bnum', this.$globalState.company_bnum);
                }

                // 디버깅: FormData 내용 확인
                for (const [key, value] of formData.entries()) {
                    if (value instanceof File) {
                        console.log(`  ${key}: File(${value.name}, ${value.size} bytes, ${value.type})`);
                    } else {
                        const preview = typeof value === 'string' && value.length > 100
                            ? value.substring(0, 100) + '...'
                            : value;
                        console.log(`  ${key}:`, preview);
                    }
                }

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/mms/sendMMS`, formData, {
                    headers: {
                        'CorpNum': this.$globalState.company_bnum,
                        'UserID': this.$globalState.popbill_id
                    },
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id
                    }
                });

                return {
                    success: true,
                    receiptNum: response.data.receiptNum
                };
            } catch (error) {
                console.error('MMS 전송 실패:', error);
                throw error;
            }
        },

        async sendKakaoMessage(sendData) {
            try {
                const templateResponse = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/kakao/listATSTemplate`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id
                    }
                });

                if (!templateResponse.data) {
                    throw new Error('알림톡 템플릿을 불러올 수 없습니다.');
                }

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/kakao/sendFTS`, {
                    CorpNum: this.$globalState.company_bnum,
                    TemplateCode: templateResponse.data[0].templateCode,
                    Sender: this.$globalState.user_phone,
                    SenderName: this.$globalState.user_name,
                    Receiver: sendData.recipient.phone,
                    ReceiverName: sendData.recipient.name,
                    Content: sendData.message,
                    UserID: this.$globalState.popbill_id,
                    PlusFriendID: '@hanqmedi'
                });

                return {
                    success: true,
                    receiptNum: response.data.receiptNum
                };
            } catch (error) {
                console.error('알림톡 전송 실패:', error);
                throw error;
            }
        },

        async sendSMSMessage(sendData) {
            try {
                if (!sendData.recipient.phone) {
                    throw new Error('수신번호가 없습니다.');
                }

                if (!sendData.message) {
                    throw new Error('메시지 내용을 입력해주세요.');
                }

                const response = await this.$axios.post(`${this.$apiUrls.hanqmedi}/popbill/sms/sendSMS`, {
                    CorpNum: this.$globalState.company_bnum,
                    Sender: this.$globalState.user_phone,
                    SenderName: this.$globalState.user_name,
                    Receiver: sendData.recipient.phone,
                    ReceiverName: sendData.recipient.name,
                    Content: sendData.message,
                    UserID: this.$globalState.popbill_id,
                    company_bnum: this.$globalState.company_bnum
                });

                return {
                    success: true,
                    receiptNum: response.data.receiptNum
                };
            } catch (error) {
                console.error('SMS 전송 실패:', error);
                throw error;
            }
        },

        // 알림톡 전송내역 조회 =====================================================================
        async sendKakaoList() {
            try {
                if (!this.$globalState.popbill_id) {
                    await this.$swal.fire({
                        icon: 'warning',
                        title: '팝빌 정보 누락',
                        html: '알림톡 전송내역을 조회하려면 팝빌 ID가 필요합니다.<br>관리자에게 문의하세요.',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/kakao/getSentKakaoListURL`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id,
                        company_bnum: this.$globalState.company_bnum
                    },
                });

                if (response.data && response.data.url) {
                    const popupWidth = 1350;
                    const popupHeight = 800;
                    const left = (window.screen.width / 2) - (popupWidth / 2);
                    const top = (window.screen.height / 2) - (popupHeight / 2);

                    window.open(
                        response.data.url,
                        'KakaoSentList',
                        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,toolbar=no,menubar=no,location=no`
                    );
                } else {
                    throw new Error('알림톡 전송내역 URL을 받아오지 못했습니다.');
                }
            } catch (error) {
                console.error('알림톡 전송내역 조회 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: error.response?.data?.message || '알림톡 전송내역을 조회하는 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },
        // 문자 전송내역 조회 =====================================================================
        async sendSMSList() {
            try {
                if (!this.$globalState.popbill_id) {
                    await this.$swal.fire({
                        icon: 'warning',
                        title: '팝빌 정보 누락',
                        html: '문자 전송내역을 조회하려면 팝빌 ID가 필요합니다.<br>관리자에게 문의하세요.',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/sms/getSentSMSListURL`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id,
                        company_bnum: this.$globalState.company_bnum
                    },
                });

                if (response.data && response.data.url) {
                    const popupWidth = 1200;
                    const popupHeight = 800;
                    const left = (window.screen.width / 2) - (popupWidth / 2);
                    const top = (window.screen.height / 2) - (popupHeight / 2);

                    window.open(
                        response.data.url,
                        'SmsSentList',
                        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,toolbar=no,menubar=no,location=no`
                    );
                } else {
                    throw new Error('문자 전송내역 URL을 받아오지 못했습니다.');
                }
            } catch (error) {
                console.error('문자 전송내역 조회 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: error.response?.data?.message || '문자 전송내역을 조회하는 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        // 팩스 전송내역 조회 =====================================================================
        async sendFaxList() {
            try {
                if (!this.$globalState.popbill_id || !this.$globalState.popbill_api_key) {
                    await this.$swal.fire({
                        icon: 'warning',
                        title: '팝빌 정보 누락',
                        html: '팩스 전송내역을 조회하려면 팝빌 ID와 API 키가 필요합니다.<br>관리자에게 문의하세요.',
                        confirmButtonText: '확인'
                    });
                    return;
                }

                const response = await this.$axios.get(`${this.$apiUrls.hanqmedi}/popbill/fax/getSentListURL`, {
                    params: {
                        CorpNum: this.$globalState.company_bnum,
                        UserID: this.$globalState.popbill_id,
                        company_bnum: this.$globalState.company_bnum
                    },
                });

                if (response.data && response.data.url) {
                    const popupWidth = 1200;
                    const popupHeight = 800;
                    const left = (window.screen.width / 2) - (popupWidth / 2);
                    const top = (window.screen.height / 2) - (popupHeight / 2);

                    window.open(
                        response.data.url,
                        'FaxSentList',
                        `width=${popupWidth},height=${popupHeight},left=${left},top=${top},scrollbars=yes,toolbar=no,menubar=no,location=no`
                    );
                } else {
                    throw new Error('팩스 전송내역 URL을 받아오지 못했습니다.');
                }
            } catch (error) {
                console.error('팩스 전송내역 조회 실패:', error);
                this.$swal.fire({
                    icon: 'error',
                    title: '조회 실패',
                    text: error.response?.data?.message || '팩스 전송내역을 조회하는 중 오류가 발생했습니다.',
                    confirmButtonText: '확인'
                });
            }
        },

        applyTemplate(content) {
            if (content.includes('{name}') || content.includes('{company}')) {
                this.message = content
                    .replace('{name}', this.recipient?.name || '')
                    .replace('{company}', this.$globalState?.company || '');
            } else {
                this.message = content;
            }
        },

        onlyNumbers(event) {
            // 숫자와 백스페이스, Delete, Tab, Arrow 키만 허용
            const char = String.fromCharCode(event.which);
            if (!/[0-9]/.test(char) && !/[8,9,27,13,46,110,190]/.test(event.keyCode) &&
                !(event.keyCode >= 35 && event.keyCode <= 40)) {
                event.preventDefault();
            }
        },

        formatDateYYYYMMDD(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}${month}${day}`;
        },

        formatDateTime(dateTimeStr) {
            if (!dateTimeStr) return '-';
            try {
                // yyyyMMddHHmmss 형식으로 오는 경우
                if (dateTimeStr.length === 14) {
                    const year = dateTimeStr.substring(0, 4);
                    const month = dateTimeStr.substring(4, 6);
                    const day = dateTimeStr.substring(6, 8);
                    const hour = dateTimeStr.substring(8, 10);
                    const minute = dateTimeStr.substring(10, 12);
                    const second = dateTimeStr.substring(12, 14);
                    return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
                }
                return dateTimeStr;
            } catch (error) {
                return dateTimeStr;
            }
        },

        formatPhoneNumber(phoneNumber) {
            if (!phoneNumber) return '';
            if (phoneNumber.startsWith('010')) {
                return phoneNumber.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
            } else if (phoneNumber.startsWith('02')) {
                return phoneNumber.replace(/(\d{2})(\d{3})(\d{4})/, '$1-$2-$3');
            } else {
                return phoneNumber;
            }
        },

        formatFaxNumber(event) {
            // 숫자만 추출
            let value = event.target.value.replace(/[^0-9]/g, '');

            // 최대 10자리로 제한
            if (value.length > 10) {
                value = value.substring(0, 10);
            }

            // 3-3-4 형식으로 포맷팅 (예: 061-725-0656)
            if (value.length > 0) {
                if (value.length <= 3) {
                    // 첫 3자리만
                    value = value;
                } else if (value.length <= 6) {
                    // 3-3 형식
                    value = value.substring(0, 3) + '-' + value.substring(3);
                } else {
                    // 3-3-4 형식
                    value = value.substring(0, 3) + '-' + value.substring(3, 6) + '-' + value.substring(6, 10);
                }
            }

            this.faxNumber = value;
        },

        closeModal() {
            this.$emit('close');
        }
    },

    watch: {
        sendMethod(newMethod) {
            // 팩스 발송 방법이 아닐 때 팩스 번호 초기화
            if (newMethod !== 'fax') {
                this.faxNumber = '';
            }
        }
    },

};
</script>